import { Elements, PaymentElement } from "@stripe/react-stripe-js"
import {
  Appearance,
  StripePaymentElement,
  StripePaymentElementChangeEvent,
} from "@stripe/stripe-js"
import { useSuspenseQuery } from "@tanstack/react-query"
import { merge } from "lodash"
import { getWebViewStripeOptions } from "source/payments/utils"
import { useComputedColors } from "source/shared/hooks/useComputedColors"
import { useStripeAppearance } from "source/shared/hooks/useStripeAppearance"
import pluralize from "source/shared/pluralize"
import {
  FREQUENCY_TYPE_TO_LABELS,
  MONTH_DAY_LABELS,
  TWICE_MONTHLY_LABELS,
  WEEK_DAY_LABELS,
} from "../constants"
import { useDonationFormContext } from "../hooks/useDonationFormContext"
import { fetchStripeCSCSecretQuery, fetchStripeObjectQuery } from "../queries"
import {
  Config,
  Currency,
  Designation,
  Frequency,
  RepeatableFrequency,
  StripeApplePayDeferredBillingWithAmountType,
  StripePaymentElementOptionsWithSavedPaymentMethod,
  StripePaymentMethod,
  Total,
} from "../types"
import { shouldSavePaymentMethod } from "../utils/paymentMethods"

// TODO: Replace with min/max obtained from donation form config
const STRIPE_PAYMENT_ELEMENT_MINIMUM_AMOUNT_CENTS = 50
const STRIPE_PAYMENT_ELEMENT_MAXIMUM_AMOUNT_CENTS = 99999999

function customizeStripeAppearance(
  appearance: Appearance,
  colors: Record<string, string>,
) {
  const { tint9, tint10 } = colors
  const donationFormCustomizations = {
    variables: {
      colorBackground: tint9,
      spacingGridRow: "0.5rem",
    },
    rules: {
      ".Input": {
        backgroundColor: tint10,
        borderColor: "transparent",
        borderRadius: "1.5rem",
      },
      ".PickerItem": {
        backgroundColor: tint10,
        borderColor: "transparent",
      },
      ".PickerItem:hover": {
        backgroundColor: tint10,
      },
    },
  }

  return merge(appearance, donationFormCustomizations)
}

type StripeElementsProviderProps = {
  amountCents: Total
  currency: Currency
  frequency: Frequency
  stripePaymentMethod?: StripePaymentMethod
  savePaymentMethodChecked: boolean
  children: NonNullable<React.ReactNode>
}

function StripeElementsProvider({
  amountCents,
  currency,
  frequency,
  stripePaymentMethod,
  savePaymentMethodChecked,
  children,
}: StripeElementsProviderProps) {
  const { config } = useDonationFormContext()

  const { data: stripeObject } = useSuspenseQuery(
    fetchStripeObjectQuery(config),
  )

  const { data: stripeCustomerSessionClientSecret } = useSuspenseQuery(
    fetchStripeCSCSecretQuery(),
  )

  const stripeElementsAppearance = useStripeAppearance()
  const { colors } = useComputedColors()
  const appearance = customizeStripeAppearance(stripeElementsAppearance, colors)

  // TODO: Obtain this via some kind of validation/util function
  const validAmount =
    amountCents >= STRIPE_PAYMENT_ELEMENT_MINIMUM_AMOUNT_CENTS &&
    amountCents <= STRIPE_PAYMENT_ELEMENT_MAXIMUM_AMOUNT_CENTS

  const savePaymentMethod = shouldSavePaymentMethod({
    frequency,
    stripePaymentMethod,
    savePaymentMethodChecked,
  })

  const elementsAmount = () => {
    if (savePaymentMethod) {
      return
    }
    return validAmount
      ? amountCents
      : STRIPE_PAYMENT_ELEMENT_MINIMUM_AMOUNT_CENTS
  }

  if (stripeObject && stripeCustomerSessionClientSecret) {
    return (
      <Elements
        stripe={stripeObject}
        options={{
          mode: savePaymentMethod ? "setup" : "payment",
          setupFutureUsage: savePaymentMethod ? "off_session" : null,
          amount: elementsAmount(),
          currency,
          customerSessionClientSecret: stripeCustomerSessionClientSecret,
          appearance,
          ...getWebViewStripeOptions(),
        }}
      >
        {children}
      </Elements>
    )
  } else {
    return null
  }
}

type ConditionalStripeElementsProviderProps = {
  condition: boolean
  amountCents: Total
  currency: Currency
  frequency: Frequency
  stripePaymentMethod?: StripePaymentMethod
  savePaymentMethodChecked: boolean
  children: NonNullable<React.ReactNode>
}

export function ConditionalStripeElementsProvider({
  condition,
  amountCents,
  currency,
  frequency,
  stripePaymentMethod,
  savePaymentMethodChecked,
  children,
}: ConditionalStripeElementsProviderProps) {
  if (condition) {
    return (
      <StripeElementsProvider
        {...{
          amountCents,
          currency,
          frequency,
          stripePaymentMethod,
          savePaymentMethodChecked,
        }}
      >
        {children}
      </StripeElementsProvider>
    )
  } else {
    return children
  }
}

export function ConfiguredStripePaymentElement() {
  const { config, dispatch } = useDonationFormContext()

  const handleReady = () => dispatch({ type: "PAYMENT_ELEMENT_READY" })

  const handleChange = (event: StripePaymentElementChangeEvent) => {
    dispatch({
      type: "SET_STRIPE_PAYMENT_METHOD",
      stripePaymentMethod: {
        id: event.value.payment_method?.id,
        type: event.value.type,
      },
    })
  }

  return (
    <PaymentElement
      options={
        {
          layout: {
            type: "accordion",
            defaultCollapsed: false,
            radios: true,
            spacedAccordionItems: true,
          },
          fields: {
            billingDetails: {
              name: "auto",
              email: "never",
              phone: "auto",
              address: "auto",
            },
          },
          defaultValues: {
            billingDetails: { name: config.default_payment_method_name },
          },
          paymentMethodOrder: [
            "apple_pay",
            "us_bank_account",
            "au_becs_debit",
            "card",
          ],
          savedPaymentMethod: {
            collapsed: false,
          },
          terms: {
            card: "never",
            usBankAccount: "never",
            auBecsDebit: "never",
          },
          wallets: {
            applePay: "auto",
            googlePay: "never",
          },
        } as StripePaymentElementOptionsWithSavedPaymentMethod
      }
      onChange={handleChange}
      onReady={handleReady}
    />
  )
}

export function updatePaymentElementApplePayOptions(
  stripePaymentElement: StripePaymentElement,
  frequency: Frequency,
  designations: Designation[],
  config: Config,
  totalCents: number,
) {
  if (frequency.type !== "once") {
    const applePayOptions = {
      deferredPaymentRequest: {
        paymentDescription: buildApplePayPaymentDescription(
          designations,
          config,
        ),
        managementURL: `${window.location.origin}/giving/profile/recurring-donations`,
        deferredBilling: {
          amount: totalCents,
          amountType: "final",
          label: buildApplePayLabel(frequency),
          deferredPaymentDate: new Date(frequency.starting + "T00:00"),
        } as StripeApplePayDeferredBillingWithAmountType,
      },
    }
    stripePaymentElement.update({ applePay: applePayOptions })
  } else {
    stripePaymentElement.update({
      applePay: { deferredPaymentRequest: null },
    })
  }
}

function buildApplePayPaymentDescription(
  designations: Designation[],
  config: Config,
) {
  if (designations.length === 1) {
    const fund = config.funds.find((f) => f.id === designations[0].fundId)

    if (fund?.name) return `Donation to ${fund.name}`
  }

  return `Donation to ${pluralize(designations.length, "fund")}`
}

function buildApplePayLabel(frequency: RepeatableFrequency) {
  return `Recurring ${FREQUENCY_TYPE_TO_LABELS[frequency.type].toLowerCase()} ${getDayInfo(frequency)}`
}

function getDayInfo(frequency: RepeatableFrequency) {
  switch (frequency.type) {
    case "monthly":
      return `(${MONTH_DAY_LABELS[frequency.day]})`
    case "weekly":
    case "every_other_week":
      return `(${WEEK_DAY_LABELS[frequency.weekday]})`
    case "twice_monthly":
      return `(${TWICE_MONTHLY_LABELS[frequency.days]})`
  }
}
